import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaHandshake,
  FaBalanceScale,
  FaAward,
  FaFileContract,
  FaUserShield,
  FaMoneyBillWave,
  FaHome,
  FaBuilding,
  FaShieldAlt,
} from "react-icons/fa";

const LegalPage = () => {
  const [legals, setLegals] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchLegals = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_legal`
        );
        const data = await response.json();
        setLegals(data.data || []);
      } catch (error) {
        console.error("Error fetching Legal", error);
      }
    };

    fetchLegals();
  }, []);

  return (
    <div className="min-h-screen bg-[#EDE9E0]">
      {/* Hero Section */}
      <div className="relative bg-[#021437] py-24 px-4 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center text-white relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-[#8F8431] mb-8 shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff]">
            <FaBalanceScale className="text-4xl" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Legal & Compliance</h1>
          <p className="text-[#C7C091] text-xl max-w-2xl mx-auto">
            Transparency, trust, and full regulatory compliance
          </p>
        </div>
      </div>

      {/* Certifications Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3A2A] mb-4">Our Certifications</h2>
            <p className="text-[#5F6A5A] max-w-xl mx-auto">
              Certified by leading regulatory authorities
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {legals.length > 0 ? (
              legals.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#EDE9E0] rounded-3xl overflow-hidden shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] group"
                >
                  <div className="relative h-64">
                    <img
                      src={cert?.photo}
                      alt={cert?.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <FaAward className="text-4xl text-[#C7C091] mb-3" />
                      <h3 className="text-white text-xl font-semibold">{cert?.title}</h3>
                    </div>
                  </div>

                  <div className="p-8">
                    <p className="text-[#385532] mb-4">
                      Certificate No: <span className="font-medium">{cert?.certification_no}</span>
                    </p>
                    <p className="text-[#5F6A5A] text-sm">
                      Issued: {cert?.issue_date 
                        ? new Date(cert.issue_date).toLocaleDateString('en-IN', { 
                            year: 'numeric', month: 'long' 
                          }) 
                        : "N/A"}
                    </p>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-20">
                <FaAward className="text-6xl text-[#8F8431]/30 mx-auto mb-6" />
                <p className="text-[#5F6A5A] text-xl">No certificates available yet</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Legal Terms Section */}
      <section className="py-20 px-4 bg-[#EDE9E0]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#2C3A2A]">Our Legal Commitments</h2>
            <p className="text-[#5F6A5A] mt-3">We operate with complete transparency and legal integrity</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "Real Estate Operations", icon: FaHome, content: "All property transactions strictly comply with local real estate laws and RERA regulations." },
              { title: "Property Documentation", icon: FaFileContract, content: "Complete and accurate documentation including title deeds, approvals, and legal clearances." },
              { title: "Client Protection", icon: FaShieldAlt, content: "Full due diligence, transparent pricing, and secure transaction processes to protect our clients." },
              { title: "Data Privacy", icon: FaUserShield, content: "Strict confidentiality of client information in compliance with all data protection laws." },
              { title: "Property Compliance", icon: FaBuilding, content: "All projects comply with building codes, zoning laws, and environmental regulations." },
              { title: "Fair Contracts", icon: FaBalanceScale, content: "Clear, fair, and transparent contracts that protect the interests of all parties." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="bg-[#EDE9E0] rounded-3xl p-8 shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] hover:shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] transition-all group"
              >
                <div className="flex gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0 shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                    <item.icon className="text-white text-3xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#2C3A2A] mb-3">{item.title}</h3>
                    <p className="text-[#385532] leading-relaxed">{item.content}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto bg-[#EDE9E0] rounded-3xl p-12 md:p-16 text-center shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
          <FaHandshake className="text-6xl text-[#8F8431] mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-[#2C3A2A] mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-[#5F6A5A] text-lg mb-8">Join a legally secure and transparent real estate ecosystem</p>
          
          <a
            href="/register"
            className="inline-block px-12 py-4 bg-[#8F8431] text-white rounded-2xl font-semibold text-lg shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] hover:scale-105 transition-all"
          >
            Register Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;